import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { AuthorizationCertPeriod, CertPeriodRangeCalcDTO, IDialogUpdateCertPeriod, ReviewPeriodType } from '@core/model';
import { AuthenticationService, AuthorizationService, AuthorizationRequestService } from '@core/service';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs/operators';
import { CertPeriodsDialogComponent } from '../cert-periods-dialog/cert-periods-dialog.component';
import { formatDate } from '@angular/common';
import { FeatureToggleService } from '@mynexus/mynexus-ui-lib';
import { FeatureFlags } from '@core/enumeration';
import { SubSink } from 'subsink';
import { EMPTY_GUID } from '@core/constants';
import moment from 'moment';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-cert-grid',
    templateUrl: './cert-grid.component.html',
    styleUrls: ['./cert-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DialogService],
    standalone: false
})
export class CertGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() authCerts: AuthorizationCertPeriod[];
  @Input() tenantId: string;
  @Input() isExternal: boolean;
  @Output() updateCertPeriod: EventEmitter<any> = new EventEmitter<any>();

  cols: any[];
  certPeriodSorted: AuthorizationCertPeriod[];
  currentCertPeriod: AuthorizationCertPeriod = null;
  showCertModal = false;
  showNextCertModal = false;
  isEligibleForNextCert = true;
  nextCertPeriod: number = null;
  emptyGuid = EMPTY_GUID;
  nextCertView: CertPeriodRangeCalcDTO;
  acp = new AuthorizationCertPeriod();
  today = new Date();
  certPeriodChangedManually = false;
  isHH30DayReviewPeriods = false;
  reviewPeriodType = ReviewPeriodType;

  private subs = new SubSink();
  private dialogRef: DynamicDialogRef;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly authorizationService: AuthorizationService,
    private readonly authorizationRequestService:AuthorizationRequestService,
    private readonly authenticationService: AuthenticationService,
    public readonly dialogService: DialogService,
    private readonly messageService: MessageService,
    private readonly featureToggleService: FeatureToggleService,
  ) { }

  ngOnInit() {
    this.certPeriodSorted = this.authCerts ? [...this.authCerts].sort((a, b) => b.certPeriod - a.certPeriod) : [];
    this.certPeriodChangedManually = this.certPeriodSorted.some(el => el.certPeriodOverridden);
    this.checkEligibility();
    this.getNextCertRange();
    this.cols = [
      { field: 'certPeriod', header: 'Cert Period', style: 'width: 10rem' },
      { field: 'effectiveFromDate', header: 'Effective From', style: 'width:  10rem' },
      { field: 'effectiveToDate', header: 'Effective To', style: 'width:  10rem' },
      { field: 'hippscode', header: 'HIPPS Code', secondField: 'additionalHippscode', style: 'width: 14rem' }
    ];

    this.subs.sink = this.featureToggleService.getFeatureAccess(FeatureFlags.HH30DayReviewPeriods)
      .pipe(finalize(() => {
        this.ref.markForCheck();
      })).subscribe((isHH30DayReviewPeriods) => {
        this.isHH30DayReviewPeriods = isHH30DayReviewPeriods;
        if (this.isHH30DayReviewPeriods) {
          this.cols = [
            { field: 'certPeriod', header: 'Cert Period', style: 'width: 10rem', isSortable: true },
            { field: 'effectiveFromDate', header: 'Effective From', style: 'width:  10rem', isSortable: true },
            { field: 'effectiveToDate', header: 'Effective To', style: 'width:  10rem', isSortable: true },
            { field: 'reviewPeriodA', header: 'Review Period A', style: 'width:  10rem' },
            { field: 'reviewPeriodB', header: 'Review Period B', style: 'width:  10rem' },
            { field: 'hippscode', header: 'HIPPS Code', secondField: 'additionalHippscode', style: 'width: 14rem', isSortable: true },
          ];
        }
      });
  }

  getNextCertRange() {
    const nextCertEffectTo = new Date(this.certPeriodSorted[0]?.effectiveToDate);
    nextCertEffectTo.setDate(nextCertEffectTo.getDate() + 1);
    this.getNextCertPeriodDateRange(nextCertEffectTo.toISOString());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.authCerts){
      this.certPeriodSorted = this.authCerts ? [...this.authCerts].sort((a, b) => b.certPeriod - a.certPeriod) : [];
      this.certPeriodChangedManually = this.certPeriodSorted.some(el => el.certPeriodOverridden);
      this.checkEligibility();
      this.getNextCertRange();
      this.ref.markForCheck();
    }
  }
  ngOnDestroy() {
    this.subs.unsubscribe();

    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  checkEligibility() {
    this.today.setHours(0, 0, 0, 0);

    const threshold = new Date(this.certPeriodSorted[0]?.effectiveToDate);
    threshold.setHours(0, 0, 0, 0);
    threshold.setDate(threshold.getDate() - 6);

    this.isEligibleForNextCert = this.today.getTime() >= threshold.getTime();
  }

  setAuthNextCertPeriod() {
    for (let index = 0; index < this.nextCertView?.reviewPeriods?.length; index++) {
      const reviewPeriod = this.nextCertView?.reviewPeriods[index];
      reviewPeriod.reviewPeriodId = this.emptyGuid;
      reviewPeriod.createdBy = this.authenticationService?.claims?.emails[0];
      this.acp.reviewPeriods = this.nextCertView?.reviewPeriods;

    }
    this.acp.authorizationCertPeriodId = this.emptyGuid;
    this.acp.authorizationId = this.certPeriodSorted[0]?.authorizationId;
    this.acp.certPeriod = this.certPeriodSorted[0]?.certPeriod + 1;
    this.acp.recertDate = moment(this.nextCertView?.effectiveFromDate).format('L');
    this.acp.effectiveFromDate = moment(this.nextCertView?.effectiveFromDate).format('L');
    this.acp.effectiveToDate = moment(this.nextCertView?.effectiveToDate).format('L');
    this.acp.isLocked = false;
    this.acp.createdBy = this.authenticationService.claims.emails[0];
    this.acp.modifiedBy = this.authenticationService.claims.emails[0];
    this.acp.createdOn = moment().utc().toISOString();
    this.acp.modifiedOn = moment().utc().toISOString();
  };

  removeHippsCode(certPeriod, hippsParam) {
    certPeriod[hippsParam] = null;
    this.currentCertPeriod = certPeriod;
    this.updateHippsCode();
  }

  onAddNextCertPeriod() {
    this.showNextCertModal = true;
    this.setAuthNextCertPeriod();
  }

  createNextCertPeriod() {
    this.showNextCertModal = false;
    this.authorizationService.insertAuthorizationCertPeriod(this.acp, this.tenantId)
      .pipe(finalize(() => {
        this.ref.markForCheck();
      }))
      .subscribe(() => {
        this.updateCertPeriod.emit();
      });
  }

  getNextCertPeriodDateRange(effectiveFromDate: string): void {
    this.subs.sink = this.authorizationRequestService.getCertPeriodRangeFromEffectiveFromDate(effectiveFromDate, this.today.toISOString())
      .subscribe(certPeriod => {
        this.nextCertView = certPeriod;
      });
  }

  handleHippsCode(hippsCode: string) {
    this.showCertModal = false;

    if (hippsCode) {
      if (!this.currentCertPeriod.hippscode && this.currentCertPeriod.additionalHippscode !== hippsCode) {
        this.currentCertPeriod.hippscode = hippsCode;
        this.updateHippsCode();
      } else if (!this.currentCertPeriod.additionalHippscode && this.currentCertPeriod.hippscode !== hippsCode) {
        this.currentCertPeriod.additionalHippscode = hippsCode;
        this.updateHippsCode();
      }
    }
  }

  updateHippsCode() {
    this.authorizationService.updateAuthorizationCertPeriod(this.tenantId, this.currentCertPeriod)
      .pipe(finalize(() => {
        this.ref.markForCheck();
      }))
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .subscribe(() => { });
  }

  onEditCertPeriod(certPeriod: AuthorizationCertPeriod) {
    this.dialogRef = this.dialogService.open(CertPeriodsDialogComponent, {
      header: 'Update Cert Period dates',
      width: '420px',
      data: certPeriod,
      focusOnShow: false,
      closable: true,
    });

    this.dialogRef.onClose.subscribe((res: IDialogUpdateCertPeriod) => {
      if (res) {
        const newCertPeriod: AuthorizationCertPeriod = {
          ...certPeriod,
          ...res,
          modifiedOn: formatDate(new Date(), 'YYYY-MM-ddTHH:mm', 'en-US'),
          modifiedBy: this.authenticationService.getEmail
        };

        this.authorizationService.updateAuthorizationCertPeriod(this.tenantId, newCertPeriod)
          .pipe(finalize(() => {
            this.ref.markForCheck();
          }))
          .subscribe(() => {
            certPeriod.effectiveFromDate = res.effectiveFromDate;
            certPeriod.effectiveToDate = res.effectiveToDate;
            this.certPeriodChangedManually = true;
            this.updateCertPeriod.emit();
            this.messageService.add({
              severity: 'info',
              summary: 'Cert Period Successfully Saved',
              detail: `Cert Period: ${certPeriod.certPeriod} was successfully saved.`,
              life: 3000
            });
          });
      }
      this.ref.markForCheck();
    });
  }
}
